import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { getPasswordPolicy } from '../../store/slices/passwordPolicy/passwordPolicy';

export interface PolicyHints {
  label: string;
  regex?: RegExp;
  min?: number;
}

export function usePasswordPolicy() {
  const dispatch = useDispatch<any>();
  const policy = useSelector((state: any) => state.PasswordPolicy_Red?.data);

  useEffect(() => {
    if (!policy) dispatch(getPasswordPolicy());
  }, [dispatch, policy]);

  const buildSchema = useCallback((): yup.StringSchema<string> => {
    let schema: yup.StringSchema<string> = yup.string().required('Password is required');

    if (policy) {
      if (policy.length > 0)
        schema = schema.min(policy.length, `Password must be at least ${policy.length} characters`);
      if (policy.capitalLetters > 0)
        schema = schema.matches(/[A-Z]/, `Must contain at least ${policy.capitalLetters} uppercase letter(s)`);
      if (policy.numbers > 0)
        schema = schema.matches(/[0-9]/, `Must contain at least ${policy.numbers} number(s)`);
      if (policy.specialCharacters > 0)
        schema = schema.matches(/[!@#$%^&*(),.?":{}|<>]/, `Must contain at least ${policy.specialCharacters} special character(s)`);
    } else {
      schema = schema
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Must contain at least one uppercase letter')
        .matches(/[0-9]/, 'Must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain at least one special character');
    }

    return schema;
  }, [policy]);

  const hints: PolicyHints[] = policy
    ? [
        ...(policy.length > 0 ? [{ label: `At least ${policy.length} characters` }] : []),
        ...(policy.capitalLetters > 0 ? [{ label: `${policy.capitalLetters} uppercase letter(s) (A-Z)`, regex: /[A-Z]/ }] : []),
        ...(policy.numbers > 0 ? [{ label: `${policy.numbers} number(s) (0-9)`, regex: /[0-9]/ }] : []),
        ...(policy.specialCharacters > 0 ? [{ label: `${policy.specialCharacters} special character(s)`, regex: /[!@#$%^&*(),.?":{}|<>]/ }] : []),
      ]
    : [
        { label: 'At least 8 characters' },
        { label: 'One uppercase letter (A-Z)', regex: /[A-Z]/ },
        { label: 'One number (0-9)', regex: /[0-9]/ },
        { label: 'One special character', regex: /[!@#$%^&*(),.?":{}|<>]/ },
      ];

  return { policy, buildSchema, hints };
}
